import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { environment } from '@environments/environment';

import { PasswordProtectionComponent } from '@shared/components/password-protection/password-protection.component';

import { RestapiService } from './restapi.service';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentAuthService {
  private userStatusSubject = new BehaviorSubject<boolean>(false);
  public userStatus$ = this.userStatusSubject.asObservable();
  // password protection dialog
  private formRef: MatDialogRef<PasswordProtectionComponent> | null = null;

  constructor(
    private dialog: MatDialog,
    private restAPI: RestapiService
  ) {
    // Check if already verified on service initialization
    this.checkUserPreference();
  }

  // Check if current environment requires user verification
  requiresAuth(): boolean {
    const env = environment.environment?.toLowerCase();
    const protectedEnvironments = ['dev', 'qa', 'preprod'];
    return protectedEnvironments.includes(env);
  }

  // Check user preference and update status
  private checkUserPreference(): void {
    // If environment doesn't require auth, set status to true and return
    if (!this.requiresAuth()) {
      this.userStatusSubject.next(true);
      return;
    }

    this.userStatusSubject.next(false);
  }

  // Verify user input by calling API
  private async verifyUserData(userId: string, userInput: string): Promise<boolean> {
    try {
      const payload = {
        userId: userId,
        validation: userInput
      };

      const response = await this.restAPI.postAPI('/v1/users/validate-temp-user', payload).toPromise();
      
      // API returns success if data is valid
      return response && response?.data?.message === 'Success';
    } catch (error) {
      console.error('Error validating user data:', error);
      return false;
    }
  }


  // Show authentication dialog and handle the flow
  async showAuthDialog(errorMessage?: string): Promise<boolean> {
    // If form is already open, don't open another
    if (this.formRef) {
      return false;
    }

    try {
      this.formRef = this.dialog.open(PasswordProtectionComponent, {
        width: '500px',
        maxWidth: '90vw',
        disableClose: true,
        hasBackdrop: true,
        panelClass: 'password-protection-dialog-container',
        backdropClass: 'password-protection-backdrop',
        data: {
          errorMessage: errorMessage || null
        }
      });

      const result = await this.formRef.afterClosed().toPromise();
      this.formRef = null;

      if (result && result.username && result.password) {
        // Show processing dialog while verifying
        const processingRef = this.dialog.open(PasswordProtectionComponent, {
          width: '500px',
          maxWidth: '90vw',
          disableClose: true,
          hasBackdrop: true,
          panelClass: 'password-protection-dialog-container',
          backdropClass: 'password-protection-backdrop',
          data: {
            isValidating: true,
            username: result.username
          }
        });

        // Call API to verify user data
        const isValid = await this.verifyUserData(result.username, result.password);
        
        // Close processing dialog
        processingRef.close();
        
        if (isValid) {
          // Set user preference flag in session storage
          this.userStatusSubject.next(true);
          
          // Clear attempt count on success
          sessionStorage.removeItem('auth_attempts');
          
          return true;
        } else {
          // Invalid data - show form again with error message
          return this.showAuthDialog('Invalid username or password. Please try again.');
        }
      } else {
        // User cancelled or closed - show form again
        return this.showAuthDialog();
      }
    } catch (error) {
      console.error('Error in user form:', error);
      this.formRef = null;
      return false;
    }
  }
}
